(ns frontend.view.entity
  (:require [clojure.set :as set]
            [common.localization :as loc]
            [common.permissions.entity-permissions :as entity-permissions]
            [common.schema.registry :as registry]
            [common.util :as cu]
            [frontend.actions.entity :as entity-actions]
            [frontend.logging :as logging]
            [frontend.routing :as routing]
            [frontend.state.current-session :as session]
            [ui.completion.new-entity :as completion-new-entity]
            [frontend.updates.entity-scope :as entity-scope]
            [frontend.ux-overhaul.page-components.navigation.events :as new-navigation-events]
            [frontend.view.modals.custom-entity-creator.model :as custom-creator-m]
            [frontend.view.modals.entity.view :as entity-modal]
            [frontend.view.modals.entry-criteria.model :as entry-criteria-m]
            [frontend.view.modals.events :as modals]
            [frontend.view.modals.select-asset-type.view :as select-asset-type]
            [re-frame.core :as rf]
            [ui.view.entry-criteria.target-picker :as target-picker]))

(def opportunity-flow-fns
  {:entity.type/fundraising-opportunity session/froppo-creation-flow?
   :entity.type/sales-opportunity session/sales-oppo-creation-flow?
   :entity.type/oppo session/deal-creation-flow?
   :entity.type/exit-opportunity session/exit-oppo-creation-flow?})

(defn open-creator!
  "Open the creator of `entity-type`. `custom-args` go to the creator modal;
   `:on-created!` (fn [new-entity-id]) runs after a create, before the default
   navigation to the new entity, and `:on-cancel!` when the type picker of a
   multi-type creator is cancelled."
  [entity-type {:keys [on-cancel! on-created!] :as custom-args}]
  (if (cu/multiple? entity-type)
    (modals/open-modal!
     select-asset-type/modal-key
     {:allowed-asset-types (->> entity-type
                                (remove (fn [et]
                                          (contains? entity-permissions/non-creatable
                                                     et))))
      :modal-title         (or (some->> custom-args
                                        :attribute
                                        (registry/get-field-schema @session/tenant-schema)
                                        :field/select-target-title
                                        :en)
                               (loc/tr :select-type))
      :select-label        (or (some->> custom-args
                                        :attribute
                                        (registry/get-field-schema @session/tenant-schema)
                                        :field/select-target-label
                                        :en)
                               (loc/tr :type))
      :on-select!          (fn [selected-type close-modal!]
                             (close-modal!)
                             (open-creator! selected-type custom-args))
      :on-cancel!          (fn [_]
                             (when (fn? on-cancel!)
                               (on-cancel!)))})
    (let [entity-type (if (coll? entity-type)
                        (first entity-type)
                        entity-type)]
      (modals/open-modal!
       (cond
         ;; pipeline and stage must be passed in order to use opportunity-curtain:
         (let [flow-fn (get opportunity-flow-fns entity-type)]
           (and flow-fn
                (flow-fn)
                (:pipeline custom-args)
                (:stage custom-args)))
         custom-creator-m/opportunity-modal-key

         (= entity-type :entity.type/investor)
         custom-creator-m/investor-modal-key

         (and (session/custom-create-config?)
              (contains? (set/union
                          #{:entity.type/named-value}
                          entity-permissions/configurable-creator-entities)
                         entity-type))
         custom-creator-m/modal-key

         :else
         entity-modal/modal-key)
       (merge {:entity-type    entity-type
               ;; Waits for whichever store the caller will read the new
               ;; entity back from: session sync for a bulk-synced type, the
               ;; OpenSearch index for everything else - which is every type
               ;; the new grid and kanban list. See ui.completion.new-entity.
               :on-processed!  completion-new-entity/wait-for-searchable!
               :on-ok!         (fn [{:keys [new-entity-id new-entity close-cb!]}]
                                 (when (fn? on-created!)
                                   (on-created! new-entity-id))
                                 (when-not (:prevent-open-entity-after-create? custom-args)
                                   ;; The entity map, not the bare id: routing
                                   ;; needs the type, and `open-entity` reads it
                                   ;; from `@session/entities` when given an id.
                                   ;; Under the new grid most types are no longer
                                   ;; bulk-synced, so a just-created one is never
                                   ;; in that cache - the lookup resolved nil and
                                   ;; routing threw before the close below ever
                                   ;; ran. The create response carries the type,
                                   ;; so hand it over directly.
                                   (routing/open-entity (or new-entity new-entity-id)))
                                 (rf/dispatch [::new-navigation-events/close-modal])
                                 (close-cb!))
               :ok-button-text (loc/tr :create)}
              custom-args)))))

(defn attempt-move-to-stage!
  "Single entry point for any UI that moves an opportunity into a different
   stage. The backend (`:entity/attempt-move-to-stage`) resolves entry
   criteria itself against live Datomic state and either moves the
   opportunity or refuses, naming what's missing - no client-side
   `@session/entities` lookup is needed to make that call, so this works
   the same whether or not `opportunity` is a fully hydrated entity-map (new
   grid's OpenSearch-backed cards never are).

   `:opportunity` and `:target-stage` accept either entity-maps (with `:db/id`
   and `:entity/type`) or raw ids; an entity-map is required for
   `:opportunity` specifically, since `:entity/type` has to travel with the
   request regardless of what's cached client-side.

   `:target-pipeline` (optional) — change-pipeline flow. When provided, the
   move switches the opportunity onto this pipeline atomically with the stage
   write, and criteria / no-target / target-picker all resolve against the
   target pipeline. Accepts an entity-map or id.

   On a refusal (`:missing-target` / `:missing-criteria`), or when the backend
   asks for a review (`:review`), this does one on-demand `entity-scope` fetch
   (root = the opportunity) to hydrate just enough of `@session/entities` for
   the existing target-picker / entry-criteria modal to render - both still
   read session reactively to build their forms; only the criteria decision
   itself moved server-side.

   `:review` is the answer of a pipeline whose `:entry-criteria-mode` opens the
   modal although nothing blocks the move. The modal then lists all items that
   the config sets for the stage. Its own submit carries `:criteria-reviewed?`,
   so the backend does not ask for the same review again. The flag does not
   waive an unmet criterion: the backend still answers `:missing-criteria`.

   Callbacks:
   - `:before-save!` — zero-arg thunk fired once the backend has confirmed the
     move, immediately before `:on-success!`.

     **Not an optimistic-UI hook, despite the name.** It used to be one: the
     old `:entity/move-to-stage` endpoint always moved the opportunity once
     structural validation passed, so \"before the write\" and \"the card is
     going to move\" were the same moment. This endpoint can instead answer
     `:missing-target` or `:missing-criteria` and open a modal, so firing on
     that boundary would apply an optimistic update for a move that has not
     happened yet, leave it applied while the modal is open, and apply it a
     second time when the flow re-enters. Hence it fires on `:moved`.
     A caller that pairs it with a revert in `:on-error!` must therefore make
     that revert conditional on the update having actually been applied - see
     `ui.view.kanban-opensearch.event/move-card-failure`, which gates on
     `[:dnd :last-move]`. Callers with an idempotent revert (setting a known
     previous value, or a membership check before undoing) are unaffected.
   - `:on-success!` — `(fn [response])` called when the backend move
     succeeds. `response` is the raw `{:status :moved :opportunity ...}`.
   - `:on-error!` — `(fn [error])` called on any failure: the RPC itself
     erroring, or the on-demand fetch (needed to render the modal/picker)
     failing.
   - `:on-cancel!` — zero-arg thunk called when the user cancels the
     criteria modal or the no-target picker before submitting; no save was
     attempted.

   See docs/functional/stage_entry_criteria/."
  [{:keys [opportunity target-stage target-pipeline
           before-save! on-success! on-error! on-cancel!]}]
  (let [opp-id (or (:db/id opportunity) opportunity)
        stage-id (or (:db/id target-stage) target-stage)
        target-pipeline-id (when target-pipeline
                             (or (:db/id target-pipeline) target-pipeline))
        body (cond-> {:target-stage-id stage-id
                      :opportunity {:db/id opp-id
                                    :entity/type (:entity/type opportunity)}}
               target-pipeline-id
               (assoc :target-pipeline-id target-pipeline-id))
        fail! (fn [error]
                (when (fn? on-error!)
                  (on-error! error)))
        ;; Both the no-target picker and the criteria modal render off
        ;; session (unchanged, per ai/sync-model.md's step-b scope cut) - one
        ;; on-demand fetch, rooted at the opportunity, hydrates whatever
        ;; either needs (the opportunity itself, its target/target-profile,
        ;; associations, all within entity-scope's 1-hop/2-hop walk).
        hydrate-then! (fn [f]
                        (entity-scope/fetch-once!
                         {:entity-id opp-id
                          :on-done (fn [_entities] (f))
                          :on-error fail!}))]
    (letfn [(submit! [submitted-body]
              ;; The modal's own submit body (built from its in-modal edits) -
              ;; re-validated by the same backend gate rather than trusted
              ;; blindly, in case the fill-in still leaves something unmet.
              ;; The user has seen the review by now, so the gate must not
              ;; open it again.
              (entity-actions/*->attempt-move-to-stage!
               (assoc submitted-body :criteria-reviewed? true)
               handle-response!
               fail!))
            (open-criteria-modal!
              [{:keys [criteria review]}]
              (hydrate-then!
               (fn []
                 (modals/open-modal!
                  entry-criteria-m/modal-key
                  {:criteria (vec criteria)
                   :review review
                   :opportunity (or (session/get-entity opp-id) opportunity)
                   :target-stage target-stage
                   :target-pipeline target-pipeline
                   :on-submit! submit!
                   :on-cancel! (fn [] (when (fn? on-cancel!)
                                        (on-cancel!)))}))))
            (handle-response!
              [{:keys [status] :as response}]
              (case status
                :moved
                (do (when (fn? before-save!)
                      (before-save!))
                    (when (fn? on-success!)
                      (on-success! response)))

                :missing-target
                (hydrate-then!
                 (fn []
                   (target-picker/open!
                    {:opportunity (or (session/get-entity opp-id) opportunity)
                     :target-pipeline target-pipeline
                     :open-creator! open-creator!
                     :on-target-set! (fn [] (attempt-move-to-stage!
                                             {:opportunity opportunity
                                              :target-stage target-stage
                                              :target-pipeline target-pipeline
                                              :before-save! before-save!
                                              :on-success! on-success!
                                              :on-error! on-error!
                                              :on-cancel! on-cancel!}))
                     :on-cancel! on-cancel!
                     :on-error! on-error!})))

                (:missing-criteria :review)
                (open-criteria-modal! response)

                (do
                  (logging/error "attempt-move-to-stage!: unexpected response " response)
                  (fail! response))))]
      (entity-actions/*->attempt-move-to-stage! body handle-response! fail!))))
